"use client";

import { Shield, Zap } from "lucide-react";

interface TrustBadgesProps {
  avgResponseTimeHours?: number | null;
  responseTimeCount?: number;
  hasPublicLiabilityInsurance?: boolean;
  pliCoverage?: string | null;
  verifiedAt?: string | null;
  layout?: "row" | "compact";
}

function ResponseTimeBadge({
  hours,
  compact,
}: {
  hours: number;
  compact: boolean;
}) {
  const label =
    hours <= 1
      ? "Responds within an hour"
      : hours <= 2
        ? "Responds within 2 hours"
        : hours <= 4
          ? "Responds within a few hours"
          : hours <= 12
            ? "Responds within half a day"
            : hours <= 24
              ? "Responds within a day"
              : "Responds within a few days";

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border ${
        compact
          ? "px-2.5 py-1 text-[10px]"
          : "px-3 py-1.5 text-xs"
      } font-semibold bg-forest/5 text-forest border-forest/15`}
    >
      <Zap className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
      {label}
    </div>
  );
}

function InsuranceBadge({
  coverage,
  compact,
}: {
  coverage?: string | null;
  compact: boolean;
}) {
  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border ${
        compact
          ? "px-2.5 py-1 text-[10px]"
          : "px-3 py-1.5 text-xs"
      } font-semibold bg-success/5 text-success border-success/15`}
    >
      <Shield className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
      Insurance self-declared{coverage && !compact ? ` (${coverage})` : ""}
    </div>
  );
}

export function TrustBadges({
  avgResponseTimeHours,
  responseTimeCount = 0,
  hasPublicLiabilityInsurance,
  pliCoverage,
  layout = "row",
}: TrustBadgesProps) {
  const compact = layout === "compact";
  const showResponseTime =
    avgResponseTimeHours != null &&
    avgResponseTimeHours > 0 &&
    avgResponseTimeHours <= 72 &&
    responseTimeCount >= 3;
  const showInsurance = !!hasPublicLiabilityInsurance;

  if (!showResponseTime && !showInsurance) return null;

  return (
    <div className="flex flex-wrap gap-2" aria-label="Trust badges">
      {showInsurance && (
        <InsuranceBadge coverage={pliCoverage} compact={compact} />
      )}
      {showResponseTime && (
        <ResponseTimeBadge hours={avgResponseTimeHours!} compact={compact} />
      )}
    </div>
  );
}
